import React from "react";
import { motion } from "framer-motion";
import { MapPin, Navigation, Phone, MessageCircle } from "lucide-react";
import { track } from "@/lib/analytics";
import { getLinks, telHref } from "@/lib/links";
import OrderButtons from "./OrderButtons";
import SocialReviewBlock from "./SocialReviewBlock";

export default function LocationSection({ settings }) {
  const loc = settings?.location || {};
  const { phone, whatsapp } = getLinks(settings);
  const query = encodeURIComponent(loc.maps_query || `${loc.name} ${loc.address} ${loc.city}`);

  const openMaps = () => {
    track("directions_click", {});
    const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
    const url = isApple
      ? `https://maps.apple.com/?q=${query}`
      : `https://www.google.com/maps/search/?api=1&query=${query}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <section id="location" className="relative bg-[#FFC20E] grain py-16 sm:py-24">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 relative z-10 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="font-display font-black uppercase display-tight text-6xl sm:text-8xl text-[#0C0C0C]" data-testid="location-title">
            DOVE SI<br />MORDE?
          </h2>
          <div className="mt-6 flex items-start gap-3 text-[#0C0C0C]" data-testid="location-address">
            <MapPin size={28} className="shrink-0 mt-1" />
            <div>
              <p className="font-display font-black uppercase text-3xl leading-none">{loc.name || "UNGRIP"}</p>
              <p className="font-semibold text-lg mt-1">{loc.address}</p>
              <p className="font-semibold text-lg">{loc.city}</p>
              {phone && (
                <a href={telHref(phone)} onClick={() => track("phone_click", { from: "location" })} className="inline-block py-2.5 -my-2.5 font-semibold text-lg underline-offset-4 hover:underline" data-testid="location-phone-number">{phone}</a>
              )}
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={openMaps}
            className="inline-flex items-center gap-2 rounded-full bg-[#0C0C0C] text-[#FFC20E] font-display font-extrabold uppercase tracking-wide text-lg px-7 py-4 hover:scale-[1.03] transition-transform duration-200"
            data-testid="location-maps-cta"
          >
            <Navigation size={20} />
            PORTAMI DA UNGRIP
          </button>
          {phone && (
            <a
              href={telHref(phone)}
              onClick={() => track("phone_click", { from: "location_cta" })}
              className="inline-flex items-center gap-2 rounded-full bg-[#0C0C0C] text-[#FFC20E] font-display font-extrabold uppercase tracking-wide text-lg px-7 py-4 hover:scale-[1.03] transition-transform duration-200"
              data-testid="location-call-cta"
            >
              <Phone size={20} />
              CHIAMACI
            </a>
          )}
          {whatsapp && (
            <a
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { from: "location_cta" })}
              className="inline-flex items-center gap-2 rounded-full bg-[#0C0C0C] text-[#FFC20E] font-display font-extrabold uppercase tracking-wide text-lg px-7 py-4 hover:scale-[1.03] transition-transform duration-200"
              data-testid="location-whatsapp-cta"
            >
              <MessageCircle size={20} />
              WHATSAPP
            </a>
          )}
          </div>
          <OrderButtons ordering={settings?.ordering} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl overflow-hidden border-4 border-[#0C0C0C] aspect-[4/3] bg-[#0C0C0C]"
        >
          <iframe
            title="Mappa UNGRIP Foggia"
            className="w-full h-full grayscale contrast-125"
            loading="lazy"
            src={`https://www.google.com/maps?q=${query}&output=embed`}
          />
        </motion.div>
      </div>
      <SocialReviewBlock settings={settings} />
    </section>
  );
}
